import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from './AuthProvider';
import { useAccessManifest } from './AccessManifestProvider';
import { safeReturnTo, splashPath, authPath } from './returnTo';
import { fetchTermsMeta } from '@/api';
import { getIntelligenicToken } from './getIntelligenicToken';
import PlansRedirect from './PlansRedirect';

interface AuthGuardProps {
    children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
    const { isAuthenticated, isLoading, error, retryAuth } = useAuthContext();
    const { getToken, isSignedIn, isLoaded } = useAuth();
    const location = useLocation();
    const requestedLocation = safeReturnTo(`${location.pathname}${location.search}${location.hash}`);

    const { manifest, loading: subscriptionLoading, error: manifestError, refresh: refreshManifest } = useAccessManifest();

    const subscriptionChecked = !subscriptionLoading;
    const hasSubscription = Boolean(manifest?.entitlements.context_mesh || manifest?.entitlements.product_studio);
    const [termsStatus, setTermsStatus] = useState<'checking' | 'accepted' | 'required' | 'error'>('checking');
    const [termsRetry, setTermsRetry] = useState(0);

    const retryTerms = useCallback(() => {
        setTermsStatus('checking');
        setTermsRetry(value => value + 1);
    }, []);

    useEffect(() => {
        if (!isLoaded) return;
        if (!isSignedIn || !isAuthenticated) {
            setTermsStatus('checking');
            return;
        }

        let cancelled = false;
        const verifyTermsAcceptance = async () => {
            try {
                const token = await getIntelligenicToken(getToken);
                if (!token) throw new Error('Missing authentication token');
                const meta = await fetchTermsMeta(token);
                if (!cancelled) {
                    setTermsStatus(meta.show_terms && meta.requires_acceptance ? 'required' : 'accepted');
                }
            } catch (cause) {
                console.error('AuthGuard: Terms verification failed:', cause);
                if (!cancelled) setTermsStatus('error');
            }
        };

        void verifyTermsAcceptance();
        return () => {
            cancelled = true;
        };
    }, [getToken, isAuthenticated, isLoaded, isSignedIn, termsRetry]);

    // Show loading while authentication or subscription check is in progress
    if (isLoading || subscriptionLoading || (isAuthenticated && termsStatus === 'checking')) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    flexDirection: 'column',
                    fontFamily: 'Arial, sans-serif'
                }}
            >
                <div role="status" aria-live="polite" style={{ marginBottom: '20px' }}>
                    Loading...
                </div>
                <div
                    style={{
                        width: '40px',
                        height: '40px',
                        border: '4px solid #f3f3f3',
                        borderTop: '4px solid #3498db',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}
                />
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    // If there's an error but user is signed in with Clerk, redirect to /auth to complete auth flow
    // This handles cases where user lands on /dashboard directly from external redirect (e.g., plans page)
    if (error && isLoaded && isSignedIn) {
        console.log('AuthGuard: Error loading profile but user is signed in, redirecting to /auth');
        return <Navigate to={authPath(requestedLocation)} replace />;
    }

    // Show error state with retry option (for cases where user is not signed in)
    if (error) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    flexDirection: 'column',
                    fontFamily: 'Arial, sans-serif',
                    padding: '20px'
                }}
            >
                <div style={{ color: 'red', marginBottom: '20px', textAlign: 'center' }}>
                    Authentication Error: {error}
                </div>
                <button
                    onClick={retryAuth}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#3498db',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginBottom: '10px'
                    }}
                >
                    Retry
                </button>
                <button
                    onClick={() => window.location.href = '/splash'}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#95a5a6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Back to Login
                </button>
            </div>
        );
    }

    // If not authenticated, redirect to splash page
    if (!isAuthenticated) {
        console.log('AuthGuard: Not authenticated, redirecting to splash');
        return <Navigate to={splashPath(requestedLocation)} replace />;
    }

    // Terms acceptance is a protected-route invariant, not just a post-login
    // screen. This prevents direct dashboard, checkout, and deep-link routes
    // from bypassing the current agreement.
    if (termsStatus === 'required') {
        return <Navigate to={authPath(requestedLocation)} replace />;
    }

    if (termsStatus === 'error') {
        return (
            <main style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24, fontFamily: 'Arial, sans-serif' }}>
                <div style={{ width: 'min(440px, 100%)', padding: 28, border: '1px solid #e2e8f0', borderRadius: 12, background: 'white', textAlign: 'center' }}>
                    <h1 style={{ margin: '0 0 12px', fontSize: 22 }}>We couldn&apos;t verify your Terms acceptance</h1>
                    <p role="alert" style={{ margin: '0 0 20px', color: '#475569', lineHeight: 1.5 }}>
                        Protected content remains hidden. Check your connection and try again.
                    </p>
                    <button type="button" onClick={retryTerms} style={{ padding: '10px 20px', border: 0, borderRadius: 6, background: '#103699', color: 'white', cursor: 'pointer' }}>
                        Retry
                    </button>
                </div>
            </main>
        );
    }

    if (manifestError) {
        return (
            <main style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24, fontFamily: 'Arial, sans-serif' }}>
                <div style={{ width: 'min(440px, 100%)', padding: 28, border: '1px solid #e2e8f0', borderRadius: 12, background: 'white', textAlign: 'center' }}>
                    <h1 style={{ margin: '0 0 12px', fontSize: 22 }}>We couldn&apos;t verify your access</h1>
                    <p role="alert" style={{ margin: '0 0 20px', color: '#475569', lineHeight: 1.5 }}>
                        Protected content remains hidden. Check your connection and try again.
                    </p>
                    <button type="button" onClick={() => void refreshManifest()} style={{ padding: '10px 20px', border: 0, borderRadius: 6, background: '#103699', color: 'white', cursor: 'pointer' }}>
                        Retry
                    </button>
                </div>
            </main>
        );
    }

    // Users without a product entitlement choose a plan on the public site.
    if (subscriptionChecked && !hasSubscription) {
        return <PlansRedirect />;
    }

    // User is authenticated and has subscription, render the protected content
    return <>{children}</>;
};

export default AuthGuard;
